import { types, getRoot, flow, detach, clone } from "mobx-state-tree";
import { db } from "../firebase.js";
import { removeInternalPropsAndFuncs } from "../helpers/index.js";
import { actions } from "./_common.js";
import { compare, unique } from "../helpers/index.js";
import { values } from "mobx";
import * as dateFns from "date-fns";
import * as H from "../helpers";
import XLSX from "xlsx";
import FirestoreDate from "./FirestoreDate";
import FirebaseStore from "./Firebase";

const $firebase = new FirebaseStore();

let ref = db.collection("surveys");
export const Survey = types.compose(
  types
    .model({
      _key: types.identifier,
      _path: types.maybeNull(types.string),
      _activeQuestion: 0,
      _disableWelcome: types.optional(types.boolean, false),
      _shareUrl: types.maybeNull(types.string),
      //_answers: types.optional(types.map(Answer2), {}),
      cluster: types.maybeNull(types.string),
      name: types.string,
      createdBy: types.maybeNull(types.string),
      createdOn: FirestoreDate,
      updatedBy: types.maybeNull(types.string),
      updatedOn: FirestoreDate,
      enabled: types.optional(types.boolean, true),
      disabledForGroups: types.frozen([]),
      disabledMessage: types.optional(types.string, "Deze vragenlijst is afgesloten."),
      isTemplate: types.optional(types.boolean, false),
      template: types.optional(types.string, ""),
      languages: types.optional(types.string, "nl_BE"),
      //interval_period: types.optional(types.string, "none"),
      //interval_start: types.maybeNull(FirestoreDate, null),
      questions_for_group: types.optional(types.boolean, false),
      educationLevel: types.maybeNull(types.string),
      respondentCount: types.optional(types.number, 0)
    })
    .actions(self => ({
      fsSave: flow(function* fsSave() {
        const { $alert, $survey, $locale, $user } = getRoot(self);
        $alert.add("loading");
        if (!self.cluster) self.change("cluster", H.slugify(self.name));
        self.change("updatedBy", $user.sessionUser.uid);
        self.change("updatedOn", new Date());
        let doc = self._key !== "new" ? ref.doc(self._key) : ref.doc();
        yield self._key !== "new"
          ? doc.update(removeInternalPropsAndFuncs(self))
          : doc.set(removeInternalPropsAndFuncs(self));
        $survey.setToList(doc.id, removeInternalPropsAndFuncs(self));
        values($locale.selectedList).forEach(locale => {
          locale.fsSave();
        });
        $alert.delete("loading");
        return doc;
      }),
      fsDelete: flow(function* fsDelete() {
        getRoot(self).$survey.deleteFromList(self._key);
        yield ref.doc(self._key).delete();
      }),
      submitAnswers: flow(function* (e) {
        const { $alert, $router, $validation, $answer } = getRoot(self);
        e.preventDefault();
        $validation.validateForm("survey-" + self._key);
        if ($validation.list.size === 0) {
          $alert.add("loading");
          try {
            yield $answer.saveTempAnswerValues();
            yield self.updateRespondentCount();
            localStorage.removeItem("barometer-answers-" + self._key);
            $alert.delete("loading");
            $router.push("/survey/results/" + self._key);
          } catch (error) {
            console.log(error);
          }
        }
      }),
      generateShareUrl: flow(function* (groupKey, mentor) {
        const root = getRoot(self);
        //want root wijzigt naar self na fetch
        if (!db.app.options.apiKey) return;
        getRoot(self).$alert.add("loading");

        let link = `http://${window.location.host}/survey/view/${self._key}/?language=${root.$locale.activeLocale}`;
        if (groupKey) link = `${link}&group=${groupKey}`;
        if (mentor) link = `${link}&mentor=true`;

        const res = yield fetch(
          "https://firebasedynamiclinks.googleapis.com/v1/shortLinks?key=" + db.app.options.apiKey,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json"
            },
            body: JSON.stringify({
              dynamicLinkInfo: {
                domainUriPrefix: "https://" + getRoot(self).$config.share_prefix + ".page.link",
                link
              }
            })
          }
        );
        const json = yield res.json();
        root.$alert.delete("loading");
        return json.shortLink;
      }),
      changeLanguages(e) {
        let languages = self.languages.split(",");
        let index = languages.indexOf(e.target.value);
        if (index > -1) {
          !e.target.checked && languages.splice(index, 1);
        } else {
          e.target.checked && languages.push(e.target.value);
        }
        self.languages = languages.join();
      },
      prevActiveQuestion(e) {
        e.preventDefault();
        //getRoot(self).$validation.validateForm('survey-' + self._key);
        self._activeQuestion--;
        window.scrollTo(0, 0);
      },
      nextActiveQuestion(e) {
        e.preventDefault();
        //getRoot(self).$validation.validateForm('survey-' + self._key);
        self._activeQuestion++;
        window.scrollTo(0, 0);
      },
      syncRespondentCount: flow(function* () {
        const { $respondent, $group, $user } = getRoot(self);
        const respondents = yield $respondent.load(self._key);
        $user.loadMeta();
        self.change("respondentCount", Object.keys(respondents).length);
        self.fsSave();

        values($group.list).forEach(function (group, i) {
          const runner = setTimeout(function () {
            if (group) {
              let _user = $user.list.get(group.createdBy);
              if (_user) {
                group.change("createdByEmail", $user.list.get(group.createdBy).email);
              }
              group.change("respondentCount", {
                ...group.respondentCount,
                [self._key]: group.responsesLength
              });
              group.fsSave();
              clearTimeout(runner);
              console.log("group :>> ", group);
            }
          }, i * 1000);
        });
      }),
      updateRespondentCount: flow(function* () {
        const { $group } = getRoot(self);
        let groupCount = 1;

        self.change("respondentCount", self.respondentCount++);

        if ($group.selected) {
          if ($group.selected.respondentCount[self._key]) {
            groupCount = Number($group.selected.respondentCount[self._key]) + 1;
          }

          $group.selected.change("respondentCount", {
            ...$group.selected.respondentCount,
            [self._key]: groupCount
          });
        }

        yield $firebase.incrementRespondentCount({
          surveyKey: self._key,
          groupKey: $group.selected ? $group.selected._key : null,
          groupRespondentCount: $group.selected && $group.selected.responsesLength
        });
      })
    }))
    .views(self => ({
      get editable() {
        const { $user } = getRoot(self);
        return $user.sessionUser.isMentor || self.responses.length;
        // const { $user, $group } = getRoot(self);
        // return !(
        //   ($user.sessionUser.isMentor &&
        //     $group.selected &&
        //     $group.selected.responses.length) ||
        //   ($user.sessionUser.isCreator && self.responses.length)
        // );
      },
      get editableShowWarning() {
        const { $user, $group, $config } = getRoot(self);
        if ($config.id === "obs-barometer") {
          if ($user.sessionUser.isMentor && $group.selected && $group.selected.respondentCount) {
            return true;
          }
        }
        return $user.sessionUser.isCreator && self.respondentCount;
      },
      get expireDate() {
        if (self.availableIntervalPeriods) {
          return self.availableIntervalPeriods[self.availableIntervalPeriods.length - 1].endDate;
        } else {
          return new Date(Date.now() + 86400000 * 365);
        }
      },
      get availableIntervalPeriods() {
        let periods = [];
        let time = "Months";
        let amount = 1;
        if (self.interval_period === "none") {
          return false;
        }
        if (self.interval_period === "day") {
          time = "Days";
          amount = 1;
        }
        if (self.interval_period === "week") {
          time = "Weeks";
          amount = 1;
        }
        if (self.interval_period === "month") {
          time = "Months";
          amount = 1;
        }
        if (self.interval_period === "trimester") {
          time = "Months";
          amount = 3;
        }
        if (self.interval_period === "semester") {
          time = "Months";
          amount = 6;
        }
        if (self.interval_period === "year") {
          time = "Years";
          amount = 1;
        }
        let difference = dateFns[`differenceIn${time}`](Date.now(), self.interval_start);

        //if (difference > 0) {
        for (let i = 0; i < difference + 1; i++) {
          let startDate = dateFns[`add${time}`](self.interval_start, i);
          let endDate = dateFns.subDays(
            dateFns[`add${time}`](self.interval_start, i + 1) * amount,
            1
          );
          periods.push({ startDate, endDate });
        }
        //} else {
        //   periods.push({
        //     startDate: dateFns.format(Date.now, 'DD/MM/YYYY'),
        //     endDate: dateFns.subDays(dateFns[`add${time}`](self.interval_start, 1) * amount, 1)
        //   });
        // }

        return periods;
      },
      get questionTree() {
        let { $group } = getRoot(self);
        let questions = self.questions;
        let tree = [];

        //tot 3 niveaus
        tree = questions
          .filter(q => q.enabled)
          .filter(
            q =>
              !self.questions_for_group ||
              (self.questions_for_group &&
                (($group.selected && $group.selected._key === q.createdForGroup) ||
                  !q.createdForGroup))
          )
          .map(q => {
            let children = questions.filter(q1 => q1.parentKey === q._key);
            children = children.map(child => {
              return {
                ...child,
                ...{ level: 1 },
                children: questions
                  .filter(q2 => q2.parentKey === child._key)
                  .sort(compare("order", "asc"))
                  .map(child => ({
                    ...child,
                    ...{ level: 2 }
                  }))
              };
            });
            q = { ...q, ...{ level: 0 }, children };
            return !q.parentKey && q;
          });
        return tree.filter(item => item);
      },
      get questions() {
        return values(getRoot(self).$question.list).sort(compare("order", "asc"));
      },
      get questionParents() {
        let { $group } = getRoot(self);
        return self.questions
          .filter(q => !q.parentKey)
          .filter(q => q.enabled)
          .filter(
            q =>
              !self.questions_for_group ||
              (self.questions_for_group &&
                (($group.selected && $group.selected._key === q.createdForGroup) ||
                  !q.createdForGroup))
          );
      },
      get questionsDisabled() {
        return self.questions.filter(q => !q.enabled);
      },
      get questionsLength() {
        let questions = self.questions;
        return (
          questions.template.filter(q => q.type !== "fieldset").length +
          questions.survey.filter(q => q.type !== "fieldset").length
        );
      },
      get noFieldsets() {
        return self.questions.filter(q => q.type !== "fieldset");
      },
      get fieldsets() {
        return self.questions.filter(q => q.type === "fieldset");
      },
      get respondents() {
        const { $respondent } = getRoot(self);
        return unique(
          values($respondent.list).filter(r => r._surveyKey === self._key),
          "createdBy"
        );
      },
      get responses() {
        const { $respondent } = getRoot(self);
        return values($respondent.list).filter(r => r._surveyKey === self._key);
      },
      get respondentCountForSelectedGroup() {
        const { $group } = getRoot(self);
        if (!$group.selected) return null;
        return $group.selected.respondentCount[self._key];
      },
      get responsesForSelectedGroup() {
        return self.responses
          .sort(compare("createdOn", "desc"))
          .filter(response =>
            getRoot(self).$group.selected
              ? response.groupKey === getRoot(self).$group.selected._key
              : true
          );
      },
      resultsToXls() {
        const { $group, $config } = getRoot(self);

        const a = getRoot(self).$question.list.get("scIwRxe3sUfDFFJofsEc");
        const a1 = a.children.find(child => child._key === "cw3IiH2F8Kv6JRGJyPH0");
        const a2 = a.children.find(child => child._key === "4Nf12jRi56OGzAQ6eYIo");

        const b = getRoot(self).$question.list.get("viaKmvn4KBxs2aLh2I0x");
        const b1 = b.children[0];
        const b2 = b.children[1];
        const b3 = b.children[2];
        const b4 = b.children[3];
        const b5 = b.children[4];

        let results = self.responsesForSelectedGroup.map(respondent => {
          let user = {};
          let uid = respondent.createdBy;
          let email = respondent.answers["ufxnATWEGy3EA4G47iIj"];
          let geboortedatum = respondent.answers["cNH0Fp7jPpCK5hGu2fJe"];
          let geslacht = respondent.answers["g5WJafXpe8lgezcu1rZB"];
          let onderwijsniveau = respondent.answers["95fu7aaidSyHP6ABrzpt"];
          let ervaring_jaren = respondent.answers["vHyStX0NWGrGfo543anY"];
          let ervaring_collegas = respondent.answers["6m0YRT1oaO5sTgogOtvm"];
          let type = respondent.answers["UiEvRf9E8OZsUsPmprFO"];
          let groupKey = respondent.groupKey;
          let created = respondent.createdOn;

          user.uid = uid;
          user.email = email ? email : "";
          user.type = type ? (type === "leerkracht" ? 1 : 0) : "";
          user.group = getRoot(self).$group.list.has(groupKey)
            ? getRoot(self).$group.list.get(groupKey).name
            : "";
          user.created = created ? dateFns.format(created, "dd MM yy HH:mm") : "";
          user.geboortedatum = geboortedatum ? geboortedatum : "";
          user.geslacht = geslacht ? geslacht : 999;
          user.onderwijsniveau = onderwijsniveau ? onderwijsniveau : 999;
          user.ervaring_jaren = ervaring_jaren ? ervaring_jaren : 999;
          user.ervaring_collegas = ervaring_collegas ? ervaring_collegas : 999;

          a1.children.forEach((q, i) => {
            user["1_a1_" + (i + 1)] = q.getAnswerValuesForIndividual(uid)[0];
          });

          user["1_a1"] = getRoot(self)
            .$question.list.get(a1._key)
            .getAverageAnswerValue({ by: "uid", value: uid });

          a2.children.forEach((q, i) => {
            user["1_a2_" + (i + 1)] = q.getAnswerValuesForIndividual(uid)[0];
          });

          user["1_a2"] = getRoot(self)
            .$question.list.get(a2._key)
            .getAverageAnswerValue({ by: "uid", value: uid });

          let answers = {};
          a1.children.forEach((child, i) => {
            let index = $config.results[0].branches.findIndex(
              branch => branch.match.indexOf(i) > -1
            );
            let answersIndividual = child.getAnswerValuesForIndividual(uid);
            if (index > -1 && !answers["branch_" + index]) answers["branch_" + index] = [];
            if (index > -1) answers["branch_" + index].push(answersIndividual);
          });

          Object.keys(answers).forEach(key => {
            if (answers[key].length) {
              user["2_a_" + H.slugify($config.results[0].branches[key.split("_")[1]].label)] =
                answers[key].reduce((a, b) => Number(a) + Number(b)) / answers[key].length;
            }
          });

          b1.children.forEach((q, i) => {
            user["3_b1_" + (i + 1)] = q.getAnswerValuesForIndividual(uid)[0];
          });

          b2.children.forEach((q, i) => {
            user["3_b2_" + (i + 1)] = q.getAnswerValuesForIndividual(uid)[0];
          });

          b3.children.forEach((q, i) => {
            user["3_b3_" + (i + 1)] = q.getAnswerValuesForIndividual(uid)[0];
          });

          b4.children.forEach((q, i) => {
            user["3_b4_" + (i + 1)] = q.getAnswerValuesForIndividual(uid)[0];
          });

          b5.children.forEach((q, i) => {
            user["3_b5_" + (i + 1)] = q.getAnswerValuesForIndividual(uid)[0];
          });

          user["4_b1"] = getRoot(self)
            .$question.list.get(b1._key)
            .getAverageAnswerValue({ by: "uid", value: uid });

          user["4_b2"] = getRoot(self)
            .$question.list.get(b2._key)
            .getAverageAnswerValue({ by: "uid", value: uid });

          user["4_b3"] = getRoot(self)
            .$question.list.get(b3._key)
            .getAverageAnswerValue({ by: "uid", value: uid });

          user["4_b4"] = getRoot(self)
            .$question.list.get(b4._key)
            .getAverageAnswerValue({ by: "uid", value: uid });

          user["4_b5"] = getRoot(self)
            .$question.list.get(b5._key)
            .getAverageAnswerValue({ by: "uid", value: uid });

          return user;
        });

        const wb = XLSX.utils.book_new();
        const ws = XLSX.utils.json_to_sheet(results);
        XLSX.utils.book_append_sheet(wb, ws, "Antwoorden per gebruiker");
        XLSX.writeFile(
          wb,
          "barometer_inzendingen_" +
            self._key +
            "_" +
            dateFns.format(Date.now(), "ddMMyy") +
            ".xlsx"
        );
      },
      // getHighestOrderValue(parentKey) {
      //   if (parentKey) {
      //     let value = 0;
      //     self.questionTree.forEach(q => {
      //       if (q._key === parentKey && q.children[q.children.length - 1]) {
      //         value = q.children[q.children.length - 1].order + 1;
      //       } else {
      //         value = 0;
      //       }
      //     });
      //     return value;
      //   } else {
      //     return self.questionTree[self.questionTree.length - 1]
      //       ? self.questionTree[self.questionTree.length - 1].order + 1
      //       : 0;
      //   }
      // },
      getActiveQuestionKey(activeQuestionIndex) {
        let activeQuestion = self.questionParents.filter(q => q.enabled)[activeQuestionIndex];
        if (!activeQuestion) return false;
        if (activeQuestion.type === "hidden")
          activeQuestion = self.questionParents[activeQuestionIndex];
        let key = activeQuestion._key;
        if (activeQuestion.logic) {
          let match = false;
          activeQuestion.logic.conditions.forEach(condition => {
            let question = getRoot(self).$question.list.get(condition.question);
            if (question.answerTemp && condition.answer === question.answerTemp.value) {
              match = true;
            }
          });
          if (match) {
            let action = activeQuestion.logic.action.split("_");
            if (action[0] === "goto") key = action[1];
          }
        }
        return key;
      },
      updateChildrenOrder() {
        getRoot(self).$question.list.forEach(q => q.change("order", q.order * 100));
        $firebase.batchUpdateQuestions(
          getRoot(self).$survey.selected._key,
          getRoot(self).$question.list,
          ["order"]
        );
      }
    })),
  actions
);

export const SurveyStore = types.compose(
  types
    .model("SurveyStore", {
      list: types.optional(types.map(Survey), {}),
      status: types.optional(types.string, "pending"),
      selected: types.maybeNull(Survey)
      //activeIntervalPeriod: types.maybeNull(types.Date)
    })
    .actions(self => {
      return {
        setSelected(surveyKey) {
          self.selected && detach(self.selected);
          if (self.list.has(surveyKey)) {
            const selected = clone(self.list.get(surveyKey));
            self.selected = selected;
          } else {
            if (surveyKey === "new") {
              self.selected = {
                _key: "new",
                name: "",
                createdBy: getRoot(self).$user.sessionUser.uid,
                createdOn: new Date(),
                updatedOn: new Date()
              };
            }
          }
        },
        // setActiveIntervalPeriod(date) {
        //   self.activeIntervalPeriod = date
        //     ? new Date(date)
        //     : self.selected.availableIntervalPeriods.pop().startDate;
        // },
        load: flow(function* load(id) {
          const { $alert, $user, $group, $loading } = getRoot(self);
          const sessionUser = $user.sessionUser;
          let groupKeys = $group.activeGroupKeys;

          $loading && $alert.add("loading");

          console.log("> load survey", id, sessionUser.isVerifiedMentor, self.selected, groupKeys);

          if (id) {
            try {
              const snapshot = yield ref.doc(id).get();
              if (snapshot.exists) {
                self.setToList(snapshot.id, snapshot.data());
                self.setSelected(snapshot.id);
                self.setProp("status", "done");
                $alert.delete("loading");
                return Promise.resolve(snapshot);
              } else {
                $alert.add("error", { label: "Survey ID not found" });
              }
            } catch (error) {
              $alert.add("error", { label: "error" });
            }
          } else {
            try {
              let snapshot = yield ref.orderBy("createdOn", "desc").get();
              snapshot.forEach(async doc => {
                self.setToList(doc.id, doc.data());
              });

              $alert.delete("loading");
              self.setProp("status", "done");
              return Promise.resolve(snapshot);
            } catch (error) {
              console.log("error", error);
              $alert.delete("loading");
              $alert.add("error", { label: error.code });
            }
          }
        })
      };
    })

    .views(self => ({})),
  actions
);
